import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { DIMENSIONS, FRAME_RULES_V1 } from '@optiflow/frame-recommendation';
import { RecommendationDimensionMode } from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useBrandsQuery } from '@/modules/catalog/api/catalog.api';
import {
  FRAME_SHAPE_CHOICES,
  MATERIAL_CHOICES,
  MAX_COLOUR_LENGTH,
  MAX_LIST_VALUES,
  RIM_TYPE_CHOICES,
  TARGET_GROUP_CHOICES,
  sameColourListed,
  type CriteriaDraft,
  type CriteriaError,
  type CriteriaErrors,
} from '../criteria';
import { ChoiceChips, FilterSection, ONLY, PREFER, PreferenceModeChoice } from './FilterControls';

interface RecommendationFiltersFormProps {
  draft: CriteriaDraft;
  onChange: (draft: CriteriaDraft) => void;
  errors: CriteriaErrors;
  currencyCode: string;
  /** Brands come from the catalogue, which this user may not be allowed to read. */
  canChooseBrands: boolean;
}

/**
 * The criteria an optician can set, as a form that sends nothing by itself.
 *
 * Typing a budget or a size never starts a request: the page applies the whole draft at once.
 */
export function RecommendationFiltersForm({
  draft,
  onChange,
  errors,
  currencyCode,
  canChooseBrands,
}: RecommendationFiltersFormProps) {
  const { t } = useTranslation();
  const message = (error: CriteriaError | undefined) =>
    error ? t(error.key, error.values) : undefined;
  const set = <K extends keyof CriteriaDraft>(key: K, value: CriteriaDraft[K]) =>
    onChange({ ...draft, [key]: value });

  return (
    <div className="space-y-5">
      <FilterSection
        legend={t('frameRecommendations.filters.budget.legend')}
        hint={t('frameRecommendations.filters.budget.hint', { currency: currencyCode })}
      >
        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t('frameRecommendations.filters.budget.min')}
            inputMode="decimal"
            autoComplete="off"
            dir="ltr"
            value={draft.budgetMin}
            onChange={(event) => set('budgetMin', event.target.value)}
            error={message(errors.budgetMin)}
          />
          <Input
            label={t('frameRecommendations.filters.budget.max')}
            inputMode="decimal"
            autoComplete="off"
            dir="ltr"
            value={draft.budgetMax}
            onChange={(event) => set('budgetMax', event.target.value)}
            error={message(errors.budgetMax)}
          />
        </div>
      </FilterSection>

      <FilterSection legend={t('frameRecommendations.filters.size.section')}>
        <Input
          label={t('frameRecommendations.filters.size.shortcut')}
          placeholder="52-18-140"
          autoComplete="off"
          dir="ltr"
          value={draft.sizeShortcut}
          onChange={(event) => set('sizeShortcut', event.target.value)}
          hint={t('frameRecommendations.filters.size.shortcutHint')}
          error={message(errors.sizeShortcut)}
        />

        <div className="space-y-2">
          <p className="text-ink text-sm font-medium">
            {t('frameRecommendations.filters.size.dimensions')}
          </p>
          {DIMENSIONS.map((dimension) => {
            const bounds = FRAME_RULES_V1.dimensions.boundsMm[dimension];
            const range = draft.ranges[dimension];
            const name = t(`frameRecommendations.dimension.${dimension}`);
            const error = message(errors[`range.${dimension}`]);
            const setRange = (end: 'min' | 'max', value: string) =>
              set('ranges', { ...draft.ranges, [dimension]: { ...range, [end]: value } });
            return (
              <div key={dimension}>
                <div className="grid grid-cols-[minmax(0,1fr)_5rem_5rem] items-center gap-2">
                  <span className="text-muted text-sm">{name}</span>
                  <input
                    aria-label={t('frameRecommendations.filters.size.from', { dimension: name })}
                    inputMode="numeric"
                    dir="ltr"
                    placeholder={String(bounds.minMm)}
                    value={range.min}
                    onChange={(event) => setRange('min', event.target.value)}
                    aria-invalid={error ? true : undefined}
                    className="border-line focus:border-brand-500 focus:ring-brand-100 w-full rounded-lg border bg-white px-2 py-2 text-center text-sm focus:ring-4 focus:outline-none"
                  />
                  <input
                    aria-label={t('frameRecommendations.filters.size.to', { dimension: name })}
                    inputMode="numeric"
                    dir="ltr"
                    placeholder={String(bounds.maxMm)}
                    value={range.max}
                    onChange={(event) => setRange('max', event.target.value)}
                    aria-invalid={error ? true : undefined}
                    className="border-line focus:border-brand-500 focus:ring-brand-100 w-full rounded-lg border bg-white px-2 py-2 text-center text-sm focus:ring-4 focus:outline-none"
                  />
                </div>
                {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
              </div>
            );
          })}
          <p className="text-muted text-xs">{t('frameRecommendations.filters.size.rangesHint')}</p>
        </div>

        <PreferenceModeChoice
          label={t('frameRecommendations.filters.size.modeLabel')}
          value={draft.dimensionMode}
          onChange={(mode) => set('dimensionMode', mode)}
          preferValue={RecommendationDimensionMode.RANK}
          onlyValue={RecommendationDimensionMode.ONLY}
          preferHint={t('frameRecommendations.filters.size.preferHint')}
          onlyHint={t('frameRecommendations.filters.size.onlyHint')}
        />
      </FilterSection>

      <FilterSection legend={t('frameRecommendations.filters.shape.legend')}>
        <ChoiceChips
          choices={FRAME_SHAPE_CHOICES}
          selected={draft.frameShapes}
          onChange={(values) => set('frameShapes', values)}
          labelFor={(shape) => t(`catalog.frameShape.${shape}`)}
        />
        <PreferenceModeChoice
          label={t('frameRecommendations.filters.shape.modeLabel')}
          value={draft.frameShapesMode}
          onChange={(mode) => set('frameShapesMode', mode)}
          preferValue={PREFER}
          onlyValue={ONLY}
          preferHint={t('frameRecommendations.filters.preferHint')}
          onlyHint={t('frameRecommendations.filters.onlyHint')}
        />
      </FilterSection>

      {canChooseBrands ? <BrandFilter draft={draft} onChange={onChange} /> : null}

      <ColourFilter draft={draft} onChange={onChange} />

      <FilterSection
        legend={t('frameRecommendations.filters.material.legend')}
        hint={t('frameRecommendations.filters.catalogueHint')}
      >
        <ChoiceChips
          choices={MATERIAL_CHOICES}
          selected={draft.materials}
          onChange={(values) => set('materials', values)}
          labelFor={(material) => t(`catalog.frameMaterial.${material}`)}
        />
      </FilterSection>

      <FilterSection
        legend={t('frameRecommendations.filters.rimType.legend')}
        hint={t('frameRecommendations.filters.catalogueHint')}
      >
        <ChoiceChips
          choices={RIM_TYPE_CHOICES}
          selected={draft.rimTypes}
          onChange={(values) => set('rimTypes', values)}
          labelFor={(rimType) => t(`catalog.rimType.${rimType}`)}
        />
      </FilterSection>

      <FilterSection
        legend={t('frameRecommendations.filters.targetGroup.legend')}
        hint={t('frameRecommendations.filters.catalogueHint')}
      >
        <ChoiceChips
          choices={TARGET_GROUP_CHOICES}
          selected={draft.targetGroups}
          onChange={(values) => set('targetGroups', values)}
          labelFor={(group) => t(`catalog.targetGroup.${group}`)}
        />
      </FilterSection>
    </div>
  );
}

function BrandFilter({
  draft,
  onChange,
}: {
  draft: CriteriaDraft;
  onChange: (draft: CriteriaDraft) => void;
}) {
  const { t } = useTranslation();
  const brands = useBrandsQuery();
  const choices = brands.data?.items ?? [];
  const names = new Map(choices.map((brand) => [brand.id, brand.name]));

  return (
    <FilterSection legend={t('frameRecommendations.filters.brand.legend')}>
      {brands.isPending ? (
        <p className="text-muted text-sm">{t('frameRecommendations.filters.brand.loading')}</p>
      ) : brands.isError ? (
        <p className="text-sm text-red-600">{t('frameRecommendations.filters.brand.error')}</p>
      ) : choices.length === 0 ? (
        <p className="text-muted text-sm">{t('frameRecommendations.filters.brand.empty')}</p>
      ) : (
        <div className="max-h-48 overflow-y-auto">
          <ChoiceChips
            choices={choices.map((brand) => brand.id)}
            selected={draft.brandIds}
            onChange={(values) =>
              onChange({ ...draft, brandIds: values.slice(0, MAX_LIST_VALUES) })
            }
            labelFor={(id) => names.get(id) ?? id}
          />
        </div>
      )}
      <PreferenceModeChoice
        label={t('frameRecommendations.filters.brand.modeLabel')}
        value={draft.brandIdsMode}
        onChange={(mode) => onChange({ ...draft, brandIdsMode: mode })}
        preferValue={PREFER}
        onlyValue={ONLY}
        preferHint={t('frameRecommendations.filters.preferHint')}
        onlyHint={t('frameRecommendations.filters.onlyHint')}
      />
    </FilterSection>
  );
}

function ColourFilter({
  draft,
  onChange,
}: {
  draft: CriteriaDraft;
  onChange: (draft: CriteriaDraft) => void;
}) {
  const { t } = useTranslation();

  const colour = draft.colourInput.trim();
  const isFull = draft.colours.length >= MAX_LIST_VALUES;
  const canAdd = colour !== '' && !sameColourListed(draft.colours, colour) && !isFull;

  const add = () => {
    if (!canAdd) return;
    onChange({ ...draft, colours: [...draft.colours, colour], colourInput: '' });
  };

  return (
    <FilterSection legend={t('frameRecommendations.filters.colour.legend')}>
      <div className="flex items-end gap-2">
        <Input
          label={t('frameRecommendations.filters.colour.input')}
          placeholder={t('frameRecommendations.filters.colour.placeholder')}
          maxLength={MAX_COLOUR_LENGTH}
          autoComplete="off"
          value={draft.colourInput}
          onChange={(event) => onChange({ ...draft, colourInput: event.target.value })}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="secondary" onClick={add} disabled={!canAdd}>
          <Plus className="size-4" aria-hidden />
          {t('frameRecommendations.filters.colour.add')}
        </Button>
      </div>

      {draft.colours.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {draft.colours.map((value) => (
            <li
              key={value}
              className="border-brand-600 bg-brand-50 text-brand-700 inline-flex min-h-9 items-center gap-1 rounded-full border ps-3 pe-1 text-sm"
            >
              <span>{value}</span>
              <button
                type="button"
                onClick={() =>
                  onChange({ ...draft, colours: draft.colours.filter((entry) => entry !== value) })
                }
                aria-label={t('frameRecommendations.filters.colour.remove', { colour: value })}
                className="hover:bg-brand-100 grid size-7 place-items-center rounded-full"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <PreferenceModeChoice
        label={t('frameRecommendations.filters.colour.modeLabel')}
        value={draft.coloursMode}
        onChange={(mode) => onChange({ ...draft, coloursMode: mode })}
        preferValue={PREFER}
        onlyValue={ONLY}
        preferHint={t('frameRecommendations.filters.preferHint')}
        onlyHint={t('frameRecommendations.filters.onlyHint')}
      />
    </FilterSection>
  );
}
